<template>
  <Card>
    <CardHeader>
      <CardTitle>5. Cashback rules</CardTitle>
      <CardDescription>
        Define how much cashback customers earn, then create the offer.
      </CardDescription>
    </CardHeader>

    <CardContent class="space-y-5">
      <div
        v-for="(rule, ri) in rules"
        :key="ri"
        class="rounded-lg border border-border p-4 space-y-4"
      >
        <div class="flex items-center justify-between">
          <p class="text-sm font-medium">Rule {{ ri + 1 }}</p>
          <Button v-if="rules.length > 1" variant="ghost" size="sm" @click="rules.splice(ri, 1)">
            <Trash2 class="w-4 h-4" /> Remove
          </Button>
        </div>

        <div class="grid gap-4 sm:grid-cols-2">
          <div class="space-y-1.5">
            <Label>Customer type</Label>
            <Select v-model="rule.customerType">
              <SelectTrigger class="w-full">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <!-- Only the two values the cashback matcher recognises: EXISTING_CUSTOMER is the
                   catch-all (any customer); NEW_CUSTOMER targets new customers. -->
              <SelectContent>
                <SelectItem value="EXISTING_CUSTOMER">All customers</SelectItem>
                <SelectItem value="NEW_CUSTOMER">New customers</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="space-y-1.5">
            <Label>Usage</Label>
            <Select v-model="rule.offerUsageType">
              <SelectTrigger class="w-full">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MULTI_USE">Multi use</SelectItem>
                <SelectItem value="SINGLE_USE">Single use</SelectItem>
                <SelectItem value="LIMITED_USE">Limited use</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div v-if="rule.offerUsageType === 'LIMITED_USE'" class="grid gap-4 sm:grid-cols-2">
          <div class="space-y-1.5">
            <Label>Transaction limit</Label>
            <Input v-model.number="rule.transactionLimit" type="number" placeholder="e.g. 3" />
          </div>
          <div class="space-y-1.5">
            <Label>Limit period (months)</Label>
            <Input v-model.number="rule.limitPeriodInMonths" type="number" placeholder="e.g. 12" />
          </div>
        </div>

        <!-- Conditions -->
        <div class="space-y-3">
          <Label>Conditions</Label>
          <div
            v-for="(cond, ci) in rule.conditions"
            :key="ci"
            class="rounded-md border border-border bg-muted/30 p-3 space-y-3"
          >
            <div class="grid gap-3 sm:grid-cols-3">
              <div class="space-y-1.5">
                <Label class="text-xs">Spend condition</Label>
                <Select v-model="cond.type">
                  <SelectTrigger class="w-full">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GREATER_THAN">Greater than</SelectItem>
                    <SelectItem value="LESS_THAN">Less than</SelectItem>
                    <SelectItem value="BETWEEN">Between</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div class="space-y-1.5">
                <Label class="text-xs">Lower bound</Label>
                <Input v-model.number="cond.lowerBound" type="number" placeholder="0" />
              </div>
              <div v-if="cond.type === 'BETWEEN'" class="space-y-1.5">
                <Label class="text-xs">Upper bound</Label>
                <Input v-model.number="cond.upperBound" type="number" placeholder="0" />
              </div>
            </div>

            <div class="grid gap-3 sm:grid-cols-3">
              <div class="space-y-1.5">
                <Label class="text-xs">Reward type</Label>
                <Select v-model="cond.value.type" @update:model-value="onRewardTypeChange(cond)">
                  <SelectTrigger class="w-full">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                    <SelectItem value="FIXED">Fixed</SelectItem>
                    <SelectItem value="PERCENTAGE_MAX">Percentage (capped)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div class="space-y-1.5">
                <Label class="text-xs">Total cashback</Label>
                <Input v-model.number="cond.value.totalValue" type="number" placeholder="0" />
              </div>
              <div v-if="cond.value.type === 'PERCENTAGE_MAX'" class="space-y-1.5">
                <Label class="text-xs">Max reward</Label>
                <Input v-model.number="cond.value.maximum" type="number" placeholder="0" />
              </div>
            </div>

            <!-- How the total cashback is split. The client can set their own cut. -->
            <div class="grid gap-3 sm:grid-cols-4">
              <div class="space-y-1.5">
                <Label class="text-xs">Customer</Label>
                <Input v-model.number="cond.value.endUserValue" type="number" placeholder="0" />
              </div>
              <div class="space-y-1.5">
                <Label class="text-xs">Supplier</Label>
                <Input v-model.number="cond.value.supplierValue" type="number" placeholder="0" />
              </div>
              <div class="space-y-1.5">
                <Label class="text-xs">Boogi</Label>
                <Input v-model.number="cond.value.boogiValue" type="number" placeholder="0" />
              </div>
              <div class="space-y-1.5">
                <Label class="text-xs">Client</Label>
                <Input v-model.number="cond.value.clientValue" type="number" placeholder="0" />
              </div>
            </div>

            <div class="flex justify-end">
              <Button
                v-if="rule.conditions.length > 1"
                variant="ghost"
                size="sm"
                @click="rule.conditions.splice(ci, 1)"
              >
                <Trash2 class="w-4 h-4" /> Remove condition
              </Button>
            </div>
          </div>

          <Button variant="outline" size="sm" @click="addCondition(rule)">
            <Plus class="w-4 h-4" /> Add condition
          </Button>
        </div>
      </div>

      <Button variant="secondary" size="sm" @click="addRule">
        <Plus class="w-4 h-4" /> Add rule
      </Button>

      <p v-if="error" class="text-sm text-destructive">{{ error }}</p>
    </CardContent>

    <CardFooter class="justify-between gap-2">
      <Button variant="outline" @click="back"> <MoveLeft class="w-4 h-4" /> Back </Button>
      <Button :disabled="submitting" @click="submit">
        <Loader2 v-if="submitting" class="w-4 h-4 animate-spin" />
        <BadgePercent v-else class="w-4 h-4" />
        {{ wizard.editingOfferId ? 'Save changes' : 'Create offer' }}
      </Button>
    </CardFooter>
  </Card>
</template>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Button,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@boogi/ui'
import { Plus, Trash2, BadgePercent, MoveLeft, Loader2 } from 'lucide-vue-next'
import { addOfferWizard } from '@/stores/addOfferWizard'
import { wizardApi } from './wizardApi'

export default {
  name: 'step-cashback',
  mixins: [wizardApi],
  components: {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardFooter,
    Button,
    Input,
    Label,
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
    Plus,
    Trash2,
    BadgePercent,
    MoveLeft,
    Loader2,
  },
  data() {
    return {
      wizard: addOfferWizard,
      rules: addOfferWizard.offerInformation.cashbackRules,
      submitting: false,
      error: '',
    }
  },
  created() {
    if (!this.wizard.offerInfoComplete) {
      this.$router.replace('/add-offer/retailer')
    }
  },
  methods: {
    emptyCondition() {
      return {
        type: 'GREATER_THAN',
        lowerBound: 0,
        upperBound: null,
        value: {
          type: 'PERCENTAGE',
          totalValue: undefined,
          maxAvailableValue: undefined,
          maximum: undefined,
          endUserValue: undefined,
          supplierValue: 0,
          boogiValue: 0,
          clientValue: undefined,
        },
        fixedDistributorCommission: true,
      }
    },
    addCondition(rule) {
      rule.conditions.push(this.emptyCondition())
    },
    // The per-transaction max only applies to PERCENTAGE_MAX; clear it otherwise (mirrors admin).
    onRewardTypeChange(cond) {
      if (cond.value.type !== 'PERCENTAGE_MAX') {
        cond.value.maximum = null
      }
    },
    addRule() {
      this.rules.push({
        customerType: 'EXISTING_CUSTOMER',
        lapsedInDays: null,
        newCustomerConsideredInMonths: 12,
        offerUsageType: 'MULTI_USE',
        transactionLimit: null,
        limitPeriodInMonths: null,
        conditions: [this.emptyCondition()],
      })
    },
    back() {
      this.$router.push('/add-offer/info')
    },
    buildRequest() {
      const info = this.wizard.offerInformation
      return {
        offerName: info.offerName.trim(),
        retailerId: this.wizard.retailer.id,
        offerType: info.offerType,
        lengthOfTime: info.lengthOfTime || null,
        spendingCapped: !!info.spendingCap,
        spendingCapAmount: info.spendingCap ? info.spendingCapAmount : null,
        hidden: !!info.hidden,
        customReporting: !!info.customReporting,
        reportingPeriod: info.customReporting ? info.reportingPeriod : null,
        customRefundPeriod: !!info.customRefundPeriod,
        refundPeriodInDays: info.customRefundPeriod ? info.refundPeriodInDays : null,
        superMatch: !!info.superMatch,
        unenrichedCreditorsStrings: info.unenrichedCreditorsStrings || [],
        internalOfferId: info.internalOfferId || null,
        // Supplier is the client (forced server-side). Keep a stable per-offer id so edits
        // upsert instead of duplicating; generate one for a brand-new offer.
        supplierOffersId: [
          info.supplierId || (this.wizard.editingOfferId ? '' : crypto.randomUUID()),
        ].filter(Boolean),
        offerValidFor: info.matchAgainst,
        channelIds: this.wizard.channels,
        cashbackRules: this.rules,
        country: info.country,
        currency: info.currency,
        displayInformation: {
          retailerName: this.wizard.retailer.retailerName,
          logo: null,
          description: info.offerDescription,
          termsAndConditions: info.offerTerms || null,
          categories: info.categories,
          banner: info.offerBanner || null,
          link: info.link,
          ctaBanner: info.ctaBanner && info.ctaBanner.image ? info.ctaBanner : null,
          sectionTitle: info.sectionTitle || null,
          sectionDescription: info.sectionDescription || null,
          ctaTiles: (info.ctaTiles || []).filter((t) => t && t.image),
          videoSectionTitle: info.videoSectionTitle || null,
          videoSectionDescription: info.videoSectionDescription || null,
          videoCta: info.videoCta && info.videoCta.videoUrl ? info.videoCta : null,
        },
      }
    },
    async submit() {
      this.error = ''
      this.submitting = true
      try {
        const offer = await this.$api(this.byooPath('/offer'), {
          method: 'POST',
          body: this.buildRequest(),
        })
        const offerId = offer?.id
        if (!offerId) throw new Error('Offer was saved but no id was returned.')
        const wasEditing = !!this.wizard.editingOfferId

        // Upload the offer logo captured in the previous step.
        const info = this.wizard.offerInformation
        if (info.offerLogoFile) {
          const formData = new FormData()
          formData.append('multipartFile', info.offerLogoFile)
          await this.$api(this.byooPath(`/offer/upload-image/${offerId}`), {
            method: 'POST',
            body: formData,
            isForm: true,
          })
        }

        // Upload the banner image if one was picked (a pasted URL rides in displayInformation).
        if (info.offerBannerFile) {
          const bannerForm = new FormData()
          bannerForm.append('multipartFile', info.offerBannerFile)
          await this.$api(this.byooPath(`/offer/upload-banner/${offerId}`), {
            method: 'POST',
            body: bannerForm,
            isForm: true,
          })
        }

        // Apply / activate — only for a new offer. An edited offer is already in the
        // program, and re-applying would create a duplicate request.
        if (!wasEditing) {
          await this.$api(`/private/api/client/activated-offer/offer/apply/${offerId}`, {
            method: 'POST',
            body: this.wizard.activateOfferRequest,
          })
        }

        const retailerId = this.wizard.retailer.id
        this.wizard.reset()
        this.$router.push(`/retailer/${retailerId}/offer/${offerId}`)
      } catch (e) {
        this.error = e.message
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>
